import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Spinner,
  Text,
  Box,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import {
  contentJobList,
  contentDetail,
  createContents,
  patchContents,
} from "../../api";

const getErrorMessage = (e, fallback = "콘텐츠 생성에 실패했습니다.") =>
  e?.response?.data?.error?.message ||
  e?.response?.data?.message ||
  e?.response?.data?.detail ||
  e?.message ||
  fallback;

export default function ContentModal({ isOpen, onClose, creator, data }) {
  const [loading, setLoading] = useState(false);
  const [contentData, setContentData] = useState(null);
  const [error, setError] = useState(null);
  const [statusText, setStatusText] = useState(null);

  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    if (!creator?.creatorProfileId) return;
    if (!data) return;

    cancelledRef.current = false;

    createAndLoad({
      campaignId: data,
      profileId: creator.creatorProfileId,
      contentId: creator.contentPlanId,
    });

    return () => {
      cancelledRef.current = true;
    };
  }, [isOpen, creator, data]);

  const createAndLoad = async ({ campaignId, profileId, contentId }) => {
    try {
      setLoading(true);
      setError(null);
      setContentData(null);
      setStatusText(null);

      let finalContentId = contentId;

      /* 0. contentPlanId 없으면 기존 생성 작업을 먼저 확인하고, 없을 때만 생성 + 패치 */
      if (finalContentId == null) {
        const listRes = await contentJobList({ campaignId, profileId });
        const latest = listRes?.data?.[0] ?? null;
        let newId = latest?.generationId ?? null;

        if (newId == null || newId === "") {
          const created = await createContents({
            campaignId,
            profileId,
            platform: "instagram",
          });

          newId = created?.data?.generationId ?? created?.generationId ?? null;
        }

        if (newId == null || newId === "") {
          throw new Error("generationId 생성 실패");
        }

        finalContentId = newId;

        await patchContents({
          campaignId,
          profileId,
          contentId: finalContentId,
        });
      }

      if (cancelledRef.current) return;

      /* 1. DB 상세 조회. 외부 API 상태 확인은 서버 스케줄러가 처리한다. */
      const detailRes = await contentDetail({
        campaignId,
        profileId,
        contentId: finalContentId,
      });

      const status = detailRes?.data?.status;

      /* 2. 완료 안 되었으면 spinner만 */
      if (status !== "COMPLETED" && status !== "completed") {
        setStatusText("콘텐츠 생성 중입니다...");
        return;
      }

      if (cancelledRef.current) return;

      setContentData(detailRes);
    } catch (e) {
      console.error(e);
      if (!cancelledRef.current) {
        setError(getErrorMessage(e));
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
      }
    }
  };

  const output = contentData?.data?.resultJson?.outputs?.[0];
  const scenes = output?.scenes || [];
  const concept = output?.content_concept;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader>콘텐츠 생성</ModalHeader>
        <ModalCloseButton />

        <ModalBody overflowY="auto" pb="24px">
          {loading && (
            <Box textAlign="center" py="40px">
              <Spinner size="lg" />
              <Text mt="12px">
                {statusText ||
                  (creator.contentPlanId
                    ? "콘텐츠 불러오는 중..."
                    : "콘텐츠 생성 중입니다...")}
              </Text>
            </Box>
          )}

          {error && !loading && (
            <Box textAlign="center" py="40px">
              <Text color="red.500">{error}</Text>
            </Box>
          )}

          {statusText && !loading && !error && (
            <Box textAlign="center" py="40px">
              <Spinner size="lg" />
              <Text mt="12px">{statusText}</Text>
            </Box>
          )}

          {output && !loading && !error && (
            <Box display="flex" flexDir="column" gap="24px">
              {/* 콘텐츠 컨셉 */}
              <Box border="1px solid #E2E8F0" borderRadius="12px" p="16px">
                <Text fontWeight="700" mb="8px">
                  콘텐츠 컨셉
                </Text>

                <Text fontSize="14px" mb="6px">
                  <b>핵심 메시지:</b> {concept.core_message}
                </Text>

                <Text fontSize="14px" mb="6px">
                  <b>타겟:</b> {concept.target_audience}
                </Text>

                <Text fontSize="14px" mb="6px">
                  <b>톤 & 무드:</b> {concept.tone_and_mood}
                </Text>

                <Box display="flex" gap="6px" flexWrap="wrap" mt="6px">
                  {concept.sub_keywords.map((k) => (
                    <Tag key={k} text={k} />
                  ))}
                </Box>
              </Box>

              {/* 씬 목록 */}
              <Box display="flex" flexDir="column" gap="16px">
                {scenes.map((scene) => (
                  <SceneCard key={scene.scene_number} scene={scene} />
                ))}
              </Box>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function SceneCard({ scene }) {
  return (
    <Box border="1px solid #E2E8F0" borderRadius="12px" p="16px">
      <Text fontWeight="700" mb="4px">
        Scene {scene.scene_number}. {scene.scene_title}
      </Text>

      <Text fontSize="13px" color="gray.500" mb="8px">
        {scene.scene_type_ko} ({scene.scene_type})
      </Text>

      {scene.generated_image_url && (
        <img
          src={scene.generated_image_url}
          alt=""
          style={{
            width: "100%",
            maxHeight: "220px",
            objectFit: "cover",
            borderRadius: "8px",
            marginBottom: "12px",
          }}
        />
      )}

      <Info label="화면 연출" value={scene.visual_guide} />
      <Info label="오디오" value={scene.audio_guide} />
      <Info label="자막" value={scene.text_overlay} />
      <Info label="연출 가이드" value={scene.direction_guide} />

      <Box display="flex" gap="8px" mt="8px">
        {scene.use_product_reference && <Badge text="제품 노출" />}
        {scene.use_persona_reference && <Badge text="페르소나 반영" />}
      </Box>
    </Box>
  );
}

function Info({ label, value }) {
  if (!value) return null;

  return (
    <Box mb="6px">
      <Text fontSize="12px" color="gray.500">
        {label}
      </Text>
      <Text fontSize="14px">{value}</Text>
    </Box>
  );
}

function Tag({ text }) {
  return (
    <Box px="8px" py="2px" bg="#EDF2F7" borderRadius="12px" fontSize="12px">
      {text}
    </Box>
  );
}

function Badge({ text }) {
  return (
    <Box
      px="8px"
      py="2px"
      bg="#E6FFFA"
      borderRadius="6px"
      fontSize="12px"
      color="#065F46"
    >
      {text}
    </Box>
  );
}
